<script>
import parse from 'date-fns/parse';
import format from 'date-fns/format';
import differenceInMinutes from 'date-fns/differenceInMinutes';
import { generateTimeSlots } from '../helpers/businessHour';
import { useAlert } from 'dashboard/composables';

const timeSlots = generateTimeSlots(30);

export default {
  components: {},
  props: {
    dayName: {
      type: String,
      required: true,
    },
    // timeSlots is now an array
    timeSlots: {
      type: Array,
      default: () => [
        {
          day: 0,
          from: '',
          to: '',
          valid: false,
          openAllDay: false,
        },
      ],
    },
  },
  emits: ['update'],
  computed: {
    fromTimeOptions() {
      return timeSlots;
    },
    toTimeOptions() {
      return [...timeSlots.filter(slot => slot !== '12:00 AM'), '12:00 AM'];
    },
    isDayEnabled: {
      get() {
        // Day is enabled if we have valid slots
        return (
          this.timeSlots.length > 0 &&
          Boolean(this.timeSlots[0].from && this.timeSlots[0].to)
        );
      },
      set(value) {
        if (value) {
          const dayIndex =
            this.timeSlots.length > 0 ? this.timeSlots[0].day : 0;
          // 1. Restore all slots saved in the current session (e.g. multi-slot days)
          const savedSlots = this.timeSlots[0]?.savedSlots;
          if (savedSlots && savedSlots.length > 0) {
            this.$emit('update', savedSlots);
            return;
          }
          // 2. Restore last known times parsed from backend (persists across saves)
          const lastFrom = this.timeSlots[0]?.lastFrom;
          const lastTo = this.timeSlots[0]?.lastTo;
          this.$emit('update', [
            {
              day: dayIndex,
              from: lastFrom || timeSlots[0], // 12:00 AM
              to: lastTo || timeSlots[16], // 08:00 AM
              valid: true,
              openAllDay: false,
            },
          ]);
        } else {
          // Disable day — save current slot data so it can be restored later
          const dayIndex =
            this.timeSlots.length > 0 ? this.timeSlots[0].day : 0;
          const activeSlots = this.timeSlots.filter(s => s.from && s.to);
          // lastFrom/lastTo carry the first slot's times for backend persistence
          const firstActive = activeSlots[0] || this.timeSlots[0];
          const lastFrom = firstActive?.from || firstActive?.lastFrom || null;
          const lastTo = firstActive?.to || firstActive?.lastTo || null;
          this.$emit('update', [
            {
              day: dayIndex,
              from: '',
              to: '',
              valid: false,
              openAllDay: false,
              ...(lastFrom && lastTo ? { lastFrom, lastTo } : {}),
              ...(activeSlots.length > 0 ? { savedSlots: activeSlots } : {}),
            },
          ]);
        }
      },
    },
    hasError() {
      return this.timeSlots.some(slot => !slot.valid);
    },
    totalHours() {
      if (!this.isDayEnabled) return '0m';

      let totalMinutes = 0;
      for (let i = 0; i < this.timeSlots.length; i += 1) {
        const slot = this.timeSlots[i];
        if (slot.openAllDay) return '24h';
        if (slot.from && slot.to && slot.valid) {
          const fromDate = parse(slot.from, 'hh:mm a', new Date());
          const toDate = parse(slot.to, 'hh:mm a', new Date());
          totalMinutes += differenceInMinutes(toDate, fromDate);
        }
      }

      const [h, m] = [Math.floor(totalMinutes / 60), totalMinutes % 60];
      return [h && `${h}h`, m && `${m}m`].filter(Boolean).join(' ') || '0m';
    },
  },
  methods: {
    formatTimeSlotText(time) {
      if (!time) return '';
      if (this.$i18n.locale === 'pt_BR') {
        try {
          const d = parse(time, 'hh:mm a', new Date());
          return format(d, 'HH:mm');
        } catch (error) {
          return time;
        }
      }
      return time;
    },
    updateSlot(index, field, value) {
      const newSlots = [...this.timeSlots];
      const slot = { ...newSlots[index] };

      if (field === 'openAllDay') {
        slot.openAllDay = value;
        if (value) {
          slot.from = '12:00 AM';
          slot.to = '11:59 PM';
          slot.valid = true;
        } else {
          slot.from = '09:00 AM';
          slot.to = '05:00 PM';
          slot.valid = true;
        }
      } else {
        slot[field] = value;
        // Re-validate
        if (slot.from && slot.to) {
          const fromDate = parse(slot.from, 'hh:mm a', new Date());
          const toDate = parse(slot.to, 'hh:mm a', new Date());
          slot.valid =
            slot.to === '12:00 AM'
              ? true
              : differenceInMinutes(toDate, fromDate) > 0;

          // Check for overlaps with other slots in the SAME day
          if (slot.valid) {
            for (let i = 0; i < newSlots.length; i += 1) {
              if (i !== index && newSlots[i].from && newSlots[i].to) {
                const otherFrom = parse(
                  newSlots[i].from,
                  'hh:mm a',
                  new Date()
                );
                const otherTo = parse(newSlots[i].to, 'hh:mm a', new Date());

                // if overlap
                if (fromDate < otherTo && toDate > otherFrom) {
                  slot.valid = false;
                  useAlert(
                    this.$t('INBOX_MGMT.BUSINESS_HOURS.DAY.OVERLAP_ERROR') ||
                      'Time intervals cannot overlap'
                  );
                  break;
                }
              }
            }
          }
        } else {
          slot.valid = false;
        }
      }

      newSlots[index] = slot;
      this.$emit('update', newSlots);
    },
    addSlot() {
      const dayIndex = this.timeSlots.length > 0 ? this.timeSlots[0].day : 0;
      const newSlots = [
        ...this.timeSlots,
        {
          day: dayIndex,
          from: timeSlots[0],
          to: timeSlots[16],
          valid: true,
          openAllDay: false,
        },
      ];
      this.$emit('update', newSlots);
    },
    removeSlot(index) {
      const newSlots = [...this.timeSlots];
      newSlots.splice(index, 1);

      // If we removed the last slot, mark day as disabled
      if (newSlots.length === 0) {
        const dayIndex = this.timeSlots.length > 0 ? this.timeSlots[0].day : 0;
        this.$emit('update', [
          {
            day: dayIndex,
            from: '',
            to: '',
            valid: false,
            openAllDay: false,
          },
        ]);
      } else {
        this.$emit('update', newSlots);
      }
    },
  },
};
</script>

<template>
  <div
    class="day-wrap py-2 gap-1 px-0 min-h-[3rem] box-content border-b border-solid border-n-weak flex flex-col items-start w-full"
  >
    <div class="flex items-start w-full gap-2">
      <!-- Checkbox & Day Name -->
      <div class="flex items-center min-w-32 pt-2">
        <label class="checkbox-wrap flex items-center mb-0 cursor-pointer">
          <input
            v-model="isDayEnabled"
            name="enable-day"
            class="m-0 mr-2"
            type="checkbox"
            :title="$t('INBOX_MGMT.BUSINESS_HOURS.DAY.ENABLE')"
          />
          <span class="text-sm font-medium">{{ dayName }}</span>
        </label>
      </div>

      <!-- Not Available Message -->
      <div
        v-if="!isDayEnabled"
        class="flex items-center flex-grow text-sm text-n-slate-11 pt-2"
      >
        <span>
          {{ $t('INBOX_MGMT.BUSINESS_HOURS.DAY.UNAVAILABLE') }}
        </span>
      </div>

      <!-- Time Slots List -->
      <div v-if="isDayEnabled" class="flex flex-col flex-grow w-full gap-2">
        <div
          v-for="(slot, index) in timeSlots"
          :key="index"
          class="flex flex-col w-full"
        >
          <div class="flex items-center w-full">
            <label
              class="checkbox-wrap flex items-center mr-6 mb-0 cursor-pointer min-w-24"
            >
              <input
                :checked="slot.openAllDay"
                class="enable-checkbox m-0 mr-1"
                type="checkbox"
                :title="$t('INBOX_MGMT.BUSINESS_HOURS.ALL_DAY')"
                @change="e => updateSlot(index, 'openAllDay', e.target.checked)"
              />
              <span class="text-sm font-medium">{{
                $t('INBOX_MGMT.BUSINESS_HOURS.ALL_DAY')
              }}</span>
            </label>

            <div class="flex items-center">
              <multiselect
                :model-value="slot.from"
                :options="fromTimeOptions"
                deselect-label=""
                select-label=""
                selected-label=""
                :placeholder="$t('INBOX_MGMT.BUSINESS_HOURS.DAY.CHOOSE')"
                :allow-empty="false"
                :disabled="slot.openAllDay"
                :custom-label="formatTimeSlotText"
                class="w-32"
                @update:model-value="val => updateSlot(index, 'from', val)"
              />
              <div class="separator-icon flex items-center px-3">
                <fluent-icon icon="subtract" type="solid" size="16" />
              </div>
              <multiselect
                :model-value="slot.to"
                :options="toTimeOptions"
                deselect-label=""
                select-label=""
                selected-label=""
                :placeholder="$t('INBOX_MGMT.BUSINESS_HOURS.DAY.CHOOSE')"
                :allow-empty="false"
                :disabled="slot.openAllDay"
                :custom-label="formatTimeSlotText"
                class="w-32"
                @update:model-value="val => updateSlot(index, 'to', val)"
              />
            </div>

            <!-- Add/Remove Icons -->
            <div class="flex items-center ml-2">
              <button
                v-if="timeSlots.length > 1"
                class="p-1 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded"
                :title="$t('Remove interval')"
                @click.prevent="removeSlot(index)"
              >
                <fluent-icon icon="dismiss" size="16" />
              </button>
              <button
                v-if="index === timeSlots.length - 1 && !slot.openAllDay"
                class="p-1 ml-1 text-blue-500 hover:bg-blue-50 rounded"
                :title="$t('Add interval')"
                @click.prevent="addSlot"
              >
                <fluent-icon icon="add" size="16" />
              </button>
            </div>
          </div>

          <div
            v-if="!slot.valid && !slot.openAllDay"
            class="date-error pt-1 pl-[7.5rem]"
          >
            <span class="error text-xs text-n-ruby-9">
              {{ $t('INBOX_MGMT.BUSINESS_HOURS.DAY.VALIDATION_ERROR') }}
            </span>
          </div>
        </div>
      </div>

      <!-- Total Hours Badge -->
      <div
        v-if="isDayEnabled && !hasError"
        class="flex items-center pt-2 ml-auto shrink-0 w-20 justify-end"
      >
        <span
          class="label bg-n-brand/10 dark:bg-n-brand/30 text-n-blue-text text-xs inline-block px-2 py-1 rounded-lg"
        >
          {{ totalHours }}
        </span>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.day-wrap::v-deep .multiselect {
  @apply m-0 w-[7.5rem];

  > .multiselect__tags {
    @apply pl-3;

    .multiselect__single {
      @apply text-sm leading-6 py-2 px-0;
    }
  }
}
</style>
